import React from 'react';
import { observer } from 'mobx-react-lite';
import { DropdownOptionType } from '@insurancemenu/imenu-components-lib/dist/types';
import { Delete, Download, Edit } from '@insurancemenu/imenu-components-lib/dist/icons';
import ActionModal from '@insurancemenu/imenu-components-lib/dist/components/modals/ActionModal';
import { ModalType } from 'global/utils/ModalsHelper';
import ResponseDocumentDelegate from 'store/pages/rfq/rfq-response/proposal-response/responseDocumentDelegate';
import { useEditResponseDocumentModalStore } from 'store/pages/rfq/rfq-response/proposal-response/editResponseDocumentModalStore';
import DocumentCardBasic from 'components/common/documents-upload/DocumentCardBasic';
import ActionsDropdown from 'components/common/general/ActionsDropdown';
import EditResponseDocumentModal from '../EditResponseDocumentModal';

interface Props {
  documentDelegate: ResponseDocumentDelegate;
}

interface ActionDropdownProps {
  downloadDocument: () => void;
  openWarningModal: () => void;
  // The method is marked as optional because the modal store may be set to null upon unmount.
  openEditDocumentModal?: () => void;
}

const ActionDropdown: React.FC<ActionDropdownProps> = ({
  downloadDocument,
  openWarningModal,
  openEditDocumentModal,
}) => {
  const dropdownOptions: DropdownOptionType[] = [
    {
      value: 'downloadDocument',
      label: 'Download Document',
      icon: Download,
      onOptionClick: downloadDocument,
    },
    {
      value: 'editDocument',
      label: 'Edit Document',
      icon: Edit,
      onOptionClick: openEditDocumentModal,
    },
    {
      value: 'deleteDocument',
      customColor: 'red',
      label: 'Delete Document',
      icon: Delete,
      onOptionClick: openWarningModal,
    },
  ];

  return <ActionsDropdown name="More Options" buttonTitle="More Options" options={dropdownOptions} />;
};

const ResponseDocumentCard: React.FC<Props> = observer(({ documentDelegate }) => {
  const { type, status, uploader, description } = documentDelegate.document;

  const warningModalStore = documentDelegate.warningModal;

  const editDocumentModalStore = useEditResponseDocumentModalStore({
    document: documentDelegate.document,
    getExistingDocumentTypes: documentDelegate.getExistingDocumentTypes,
    updateDocument: documentDelegate.editResponseDocument,
  });

  const openWarningModal = (): void => warningModalStore.open();
  const closeWarningModal = (): void => warningModalStore.close();

  const onRemoveDocument = async (): Promise<void> => {
    closeWarningModal();
    await documentDelegate.removeDocument();
  };

  return (
    <>
      <DocumentCardBasic
        type={type}
        status={status}
        uploader={uploader}
        description={description}
        actionDropdown={
          <ActionDropdown
            openWarningModal={openWarningModal}
            downloadDocument={documentDelegate.downloadDocument}
            openEditDocumentModal={editDocumentModalStore?.openEditDocumentModal}
          />
        }
      />
      {editDocumentModalStore && editDocumentModalStore.showEditDocumentModal && (
        <EditResponseDocumentModal modalStore={editDocumentModalStore} />
      )}

      <ActionModal
        title="Delete Uploaded Document"
        subtitle={
          <span>
            This action will permanently delete the proposal document
            <br /> and cannot be undone.
          </span>
        }
        show={warningModalStore.isVisible}
        onClose={closeWarningModal}
        closeButtonText="Cancel"
        actionButtonText="Delete"
        onAction={onRemoveDocument}
        modalType={ModalType.DELETE}
      />
    </>
  );
});

export default ResponseDocumentCard;
